/**
 * Plain `YYYY-MM-DD` calendar helpers shared by the two calendar pickers
 * (`CustomDatePicker` + `DateRangePicker`) in `shared/ui/components`.
 *
 * These are *string-domain* date helpers — they never tz-shift. For rendering
 * stored UTC timestamps in a workshop's timezone use `lib/datetime.ts` instead;
 * for locale-aware month/day NAMES the tenant resolves them via `t()` and passes
 * them into the pickers as props (admin uses the Spanish defaults baked into the
 * components).
 */

/** `YYYY-MM-DD` from a (year, 0-based monthIndex, day) triple. */
export function toYmd(year: number, monthIndex: number, day: number): string {
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** `dd/mm/yyyy` for display in triggers and cells. */
export function formatDisplayYmd(ymd: string): string {
    if (!ymd) return '';
    const [y, mo, da] = ymd.split('-');
    if (!y || !mo || !da) return '';
    return `${da}/${mo}/${y}`;
}

/** Today as `YYYY-MM-DD` in the browser's local timezone (filter domain). */
export function todayYmd(): string {
    const t = new Date();
    return toYmd(t.getFullYear(), t.getMonth(), t.getDate());
}

export function startOfMonthYmd(d: Date): string {
    return toYmd(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonthYmd(d: Date): string {
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return toYmd(last.getFullYear(), last.getMonth(), last.getDate());
}

export function addDaysYmd(ymd: string, delta: number): string {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(y || 0, (m || 1) - 1, (d || 0) + delta);
    return toYmd(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

export function getDecadeRange(year: number): { start: number; end: number } {
    const startOfDecade = Math.floor(year / 10) * 10;
    return { start: startOfDecade, end: startOfDecade + 9 };
}
